import React from 'react'

export const episodes = [
  {
    slug: '1-webpack-from-first-principles',
    title: 'Webpack from First Principles',
    youtube: 'WQue1AN93YU',
    releaseDate: '22nd August 2016',
    duration: '14:17',
    description: ({ P, Outer }) => <Outer>
      <P>
        Webpack often gets a bad rap in the front end community — plenty of digital ink
        has been spilled over whether web development is "too complicated", with Webpack
        taking center stage. But in reality, <strong>it's no more complex than the sites
        we're building with it</strong>, and conceptually its role is quite clear.
      </P>
      <P>
        Let's demystify the tool by stripping it back to what it truly is — <strong>an
        ahead-of-time compiler for the browser</strong>.
      </P>
    </Outer>,
    ticks: ({ List, Item }) => <List>
      <Item>What Webpack does & why it's <strong>not going away</strong> any time soon.</Item>
      <Item>Where Webpack fits into the <strong>modern tooling landscape</strong>.</Item>
      <Item>How to <strong>convert a JavaScript file</strong> to work with Webpack's loaders</Item>
      <Item>The impact that using Webpack can make on <strong>performance</strong></Item>
    </List>
  },
  {
    slug: '2-current-color',
    title: 'Current Color — The First CSS Variable',
    youtube: 'krbKkLPXwlQ',
    duration: '13:47',
    releaseDate: '5th September 2016',
    description: ({ P, Outer }) => <Outer>
      <P>
        The <code>currentColor</code> keyword is a wildly powerful yet sadly
        underused feature of CSS. And understanding it requires a deep look at
        some of the core concepts in CSS that are often glossed over. We'll look
        at concepts that were present in the very first draft of CSS1 in 1996 all
        the way to the almost-ready-to-use CSS Variables spec.
      </P>
      <P>
        And with our new-found dynamism we'll show how to go past the superficial
        limitations of <code>currentColor</code> to build some contextually-themed
        real-world UI elements.
      </P>
    </Outer>
  },
  {
    slug: '3-why-inline-svg-is-best-svg',
    title: 'Why Inline SVG is Best SVG',
    duration: '15:44',
    youtube: 'af4ZQJ14yu8',
    banner: require('./assets/ep3/banner.png'),
    images: [
      require('./assets/ep3/three-modes.png'),
      require('./assets/ep3/mode-comparison.png'),
      require('./assets/ep3/focus-indicator.png'),
      require('./assets/ep3/validity-indicator.png')
    ],
    releaseDate: '22nd September 2016',
    description: ({ P, Outer }) => <Outer>
      <P>
        SVG is one of the most powerful tools in a front-end developer's arsenal,
        and while its browser support is excellent, there's enough rough edges that
        a lot of people consistently reach for an alternative such as icon fonts, static
        images or CSS-only drawing techniques.
      </P>
      <P>
        In this episode we'll look at what can be achieved by <strong>writing SVG by
        hand</strong>, simple effects that can add a lot to an interaction that only
        need a handful of lines of SVG + CSS. But we'll also consider why it is that
        Inline SVG, in particular, <strong>is so much easier to work with</strong> than
        embedding SVG in other ways.
      </P>
    </Outer>
  },
  {
    slug: '4-automating-an-inline-svg-workflow',
    title: 'Automating an Inline SVG Workflow',
    releaseDate: '5th October 2016',
    banner: require('./assets/ep4/banner.png'),
    description: ({ P, A, Outer }) => <Outer>
      <P>
        Building on last episode, we cover a seriously compelling workflow
        blending the best of both worlds — <strong>authoring an SVG spritesheet in
        Sketch but injecting them as Inline SVG</strong>, without a bunch of manual
        steps in between.
      </P>
    </Outer>,
    ticks: ({ List, Item }) => <List>
      <Item>
        <strong>How Sketch exports SVG</strong> — how objects, layers, masks and artboards
        are represented in the output
      </Item>
      <Item>
        How to structure a spritesheet with a series of icons that <strong>can be loaded
        as one but used individually</strong>.
      </Item>
      <Item>
        Javascript to load the SVG, parse it efficiently and inject <strong>only the markup
        that's required</strong>.
      </Item>
      <Item>
        <strong>The optimisations that are safe to use</strong> that don't destroy crucial
        information about the SVG file's structure.
      </Item>
      <Item>
        How to tune the rendering of the output for <strong>retina vs non-retina displays</strong>
        , and transition colours or fills based on user input.
      </Item>
    </List>
  },
  {
    slug: '5-image-cdns-to-the-rescue',
    title: 'Image CDNs to the Rescue',
    youtube: '_lQvw2vSDbs',
    releaseDate: '24th October 2016',
    banner: require('./assets/ep5/banner.png'),
    images: [
      require('./assets/ep5/image-graph.png'),
      require('./assets/ep5/srcset-demo.jpg'),
      require('./assets/ep5/jpeg-demo.jpg'),
      require('./assets/ep5/three-images.jpg'),
    ],
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        Responsive imagery is one of those topics that seems way more complicated than it aught
        to be. It’s the sort of problem that confronts virtually every website out there—every
        site has images—and yet the approaches offered by HTML alone aren't sufficient.
      </P>
      <P>
        In this episode we'll look at how using a dedicated Image CDN can simplify your markup,
        enable pixel-perfection regardless of device, use the best possible image formats available,
        and simplify use of the
        <pre>&lt;picture&gt;</pre>
        element and
        <pre>srcset</pre>
        attribute.
      </P>
    </Outer>,
    ticks: ({ List, Item }) => <List>
      <Item>That because <strong>images account for almost two thirds</strong> of the average website, they offer the biggest potential improvements.</Item>
      <Item>That pairing <strong>beautiful imagery and high performance</strong> is not only possible but easy.</Item>
      <Item>Why the
        <pre>&lt;picture&gt;</pre>
        element and
        <pre>srcset</pre>
        attribute <strong>quickly break down</strong>—becoming awkward to use and overly repetitive.</Item>
      <Item>How to configure your images to be <strong>served through dedicated infrastructure</strong>.</Item>
      <Item>How to <strong>dynamically crop & resize</strong> your images on the fly, keeping the most visually-interesting features in the frame.</Item>
    </List>
  },
  {
    slug: '6-single-responsibility-components',
    title: 'Single Responsibility Components',
    youtube: 'pSdp92Up8O8',
    releaseDate: '18th November 2016',
    banner: require('./assets/ep6/banner.png'),
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        The Functional Programming (FP) aspects of React are fairly prominent: by making your UI a pure function of your application state you remove a whole class of bugs that plagued earlier frameworks. If you look closely at the render method, or performance optimisations like shouldComponentUpdate, as well as popular libraries like Redux, again you’ll see a strong FP influence. But that’s not the only thing at play.
      </P>
      <P>
        Because component-driven design is actually much closer to Object-Oriented (OO) programming. Components encapsulate state, present a public API, and map to objects in the “real world” (or in our case the UI), which are all core properties of Objects in OO. And they relate to each other, they share information or depend upon each other, which are all classical OO design problems. It means there’s a wealth of ideas, discussions and patterns that we can bring to bear in making our React components more readable, reusable, and maintainable.
      </P>
    </Outer>,
    ticks: ({ List, Item }) => <List>
      <Item>What the Single Responsibility Principle is and how it applies to working with UI components.</Item>
      <Item>The four main sources of change and how to mitigate them.</Item>
      <Item>How to identify potential refactoring opportunities as you make a change.</Item>
      <Item>How these principles endure beyond simply being applied to React or another framework you might be using today.</Item>
    </List>
  },
  {
    slug: '7-really-reusable-components',
    title: 'Really Reusable Components',
    releaseDate: '29th November 2016',
    banner: require('./assets/ep7/banner.png'),
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        Following on from the previous episode, this time we dig into the ideas of Interface Segregation and Liskov Substitution — two more Object-Oriented design principles that are remarkably relevant to working in a component-driven UI framework. We start to pick apart some of the reasons some components feel really inflexible and some seem to work everywhere, and give you some ideas about how to convert the former into the latter in future.
      </P>
    </Outer>
  },
  {
    slug: '8-crafting-webfont-fallbacks',
    title: 'Crafting Webfont Fallbacks',
    releaseDate: '24th December 2016',
    banner: require('./assets/ep8/banner.png'),
    youtube: 'tO01ul1WNW8',
    images: [
      require('./assets/ep8/impact.png'),
      require('./assets/ep8/snapshot-loading.png'),
      require('./assets/ep8/overlapping.png'),
      require('./assets/ep8/overrides.png')
    ],
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        Webfonts are a tricky thing to get right — the usual formula is: attractive visuals, good performance & reliable functionality (pick two). This episode is about how you can get all three by properly employing a fallback font while the webfont loads.
      </P>
      <P>
        This episode comes in two parts: loading Webfonts properly & tuning the fallback font's CSS. This second part is an often-overlooked part of the process, and leads a lot of projects to conclude that the best way to use Webfonts is to hide the text until the font has loaded. And, as anyone who has stared at a blank screen on their mobile for 10+ seconds will tell you, that's not ideal.
      </P>
      <P>
        Learn how you can cater for the unpredictable circumstances of your users without compromising the visual quality of your build.
      </P>
    </Outer>
  },
  {
    slug: '9-foundations-of-web-performance-latency',
    title: 'Foundations of Web Performance — Latency',
    releaseDate: '23rd January 2017',
    banner: require('./assets/ep9/banner.png'),
    youtube: 'ak4EZQB4Ylg',
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        To kick off Front End Center for the new year we're going back to the fundamentals of web performance, and in particular the effect of latency on the speed of your site. Depending on where you live and the devices you use, high-latency internet access might be a totally foreign concept or an everyday reality, so in this episode we look at some concrete numbers to get a sense of just how much it can change someone's experience.
      </P>
      <P>
        This is the first in a new series building up <A href="http://pithy.af" target="_blank">pithy.af</A>, a side-project of mine that we'll use as a base to dive into lots of topics throughout the year. In this episode we look at the results of an experiment that I've been running, the TCP design decisions that cause connections to "slow start", and why it's worth adding a CDN to our architecture right at the beginning of the project.
      </P>
    </Outer>
  },
  {
    slug: '10-foundations-of-web-performance-caching',
    title: 'Foundations of Web Performance — Caching & CDNs',
    releaseDate: '31st January 2017',
    banner: require('./assets/ep10/banner.png'),
    youtube: '_QeNLrkPvdI',
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        Last time out we were looking at how latency puts an upper limit on the performance of your site, and just how important it is to be hosting your content near your users. This time we go a step further: talking about the techniques and tradeoffs for highly cache-optimised sites, from content-addressable storage to federating your content across a CDN so requests don't even reach your server.
      </P>
      <P>
        With just a few judicious build-time choices and deploy-time hooks, you too can have the fastest static site the web can offer.
      </P>
    </Outer>
  },
  {
    slug: '11-the-fastest-webfont-loader-in-the-west',
    title: 'The Fastest Webfont Loader in the West',
    releaseDate: '31st January 2017',
    banner: require('./assets/ep11/banner.png'),
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        About 18 months ago, I noticed that refreshing a page with Typekit fonts would cause a momentary jump—the fallback font would be rendered for only a few milliseconds before the web fonts would snap in. It annoyed me, and I started digging. Fast forward to now, and solving this problem has taken months of research and 3 whole episodes of groundwork, but we're finally there.
      </P>
      <P>
        Join me as we finally defeat the Micro-Flash-of-Unstyled-Text-Even-When-We-Have-the-Fonts-Cached.
      </P>
    </Outer>
  },
  {
    slug: '12-demystifying-z-index',
    title: 'Demystifying Z-Index',
    releaseDate: '28th February 2017',
    banner: require('./assets/ep12/banner.png'),
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        The way a browser paints, in particular the z-index property itself, occupy an unusual spot in our collective knowledge. Most of the time, things work well enough that we never really feel the need to consider the underlying mechanics, but we still vaguely know there's more than meets the eye.
      </P>
      <P>
        One day you'll have multiple layers seamlessly blending on a page, the next you won't be able to figure out why a click event is reaching the wrong element. Something's in the way, our element needs to be on top.
      </P>
      <P>
        Like any good arms race, escalation is the smart choice, and so z-indexes of 10 turn into 100, 100 turns into 1000, and soon enough we can't distinguish real code from falling asleep on the keyboard.
      </P>
      <P>
        It doesn't have to be this way. By digging down to the root of the problem, we can understand what's really going on. After all, there's only 3 really fundamental z-index values. How hard can it be?
      </P>
    </Outer>
  },
  {
    slug: '13-the-power-of-blend-modes-on-the-web',
    title: 'The Power of Blend Modes on the Web',
    releaseDate: '31st March 2017',
    banner: require('./assets/ep13/banner.png'),
    description: ({ P, List, A, Outer }) => <Outer>
      <P>
        If you've used Photoshop or basically any other image processing application this century you've probably come across the concept of blending—mixing the colours of two layers beyond what's possible with opacity. You may also be vaguely aware that this feature has made its way into most modern browsers, <strong>but why is it useful?</strong>
      </P>
      <P>
        If you're like me, you might assume that these effects are only useful for images, and, even then, only for the kind of dramatic effects that look good in a tech demo but, frankly, seem out of place in building products or pages day-to-day.
      </P>
      <P>
        But blending and compositing (the subject of the next episode) is much more than that. <strong>It's a gateway into an internal browser process</strong> that completely changes the way a website gets displayed. And gives us an incredible potential for rich, performant web designs that haven't been possible before.
      </P>
      <P>
        For this episode, though, we're going to make sure we understand the mechanics of what's going on. And answer the question—<strong>what kind of blend modes actually produce something that looks good?</strong>
      </P>
    </Outer>
  },
  {
    slug: '14-compositing-the-browsers-new-superpower',
    title: `Composition: The Browser's New Superpower`,
    releaseDate: '25th April 2017',
    banner: require('./assets/ep14/banner.png'),
    description: ({ P, Code, Outer }) => <Outer>
      <P>
        This episode is about the impact that <Code>mix-blend-mode</Code> could have on web design. At first glance, you might think that it isn't too different from the <Code>background blend-mode</Code> property we dug into in the last episode, but you'd be dead wrong. While the blend modes and the maths are the same, <Code>mix-blend-mode</Code> represents a monumental shift in the capabilities of web developers.
      </P>
      <P>
        'Compositing' refers to two things. First, it's what the browser does to paint a webpage—building up each layer depending on its stacking context and <Code>z-index</Code>, blending them based on opacity and blend mode. But it's also the term for crafting a particular visual design—which layers we put where, how they respond to scrolling, etc. With a deep understanding of the former we open up totally new possibilities for the latter.
      </P>
      <P>
        After all, it could be argued that Blending is really just "Photoshop in CSS". But Compositing? Well, that's a whole different beast altogether.
      </P>
    </Outer>
  },
  {
    slug: '15-the-hidden-costs-of-client-side-rendering',
    title: `The Hidden Costs of Client-Side Rendering`,
    releaseDate: '5th June 2017',
    banner: require('./assets/ep15/banner.png'),
    images: [
      require('./assets/ep15/html-structure.png'),
      require('./assets/ep15/division.png'),
      require('./assets/ep15/snapshot-rendering.png'),
      require('./assets/ep15/parse-times.png')
    ],
    description: ({ P, Code, Outer }) => <Outer>
      <P>
        There are undeniable benefits in using a JavaScript-centric development workflow—to building your UI as code & rendering on the client, rather than serving static markup & using JS just for interactivity. You get to draw your own boundaries along functional lines rather than language ones, wrapping things up in encapsulated components that can be reused in multiple places. You have more powerful ways of utilising libraries than simply injecting multiple script tags on the page. And you can bundle everything up using tools like Webpack that can seamlessly handle assets of all different types, applying specific tools and optimisations to each.
      </P>
      <P>
        There are costs too, of course, but it's often a lot harder to enumerate those, since they differ so markedly depending on the particular use-case. But since it's such a controversial topic, discussions can quickly get sidetracked along ideological lines. I've seen it result in a vague convention: for public-facing marketing pages, use static HTML, CSS & unobtrusive JavaScript; for your main app behind your login screen, use a JS framework and a single-page app. It's a reasonable compromise, but we can do better.
      </P>
      <P>
        With tools like server-side or snapshot rendering, we can take two seemingly opposed workflows and unify them in a way that gets the best of both worlds. To start, however, we need to better understand the problem—to dispassionately look at what we've lost without noticing, and conversely, what we now have to gain.
      </P>
    </Outer>
  },
  {
    slug: '16-snapshot-rendering-the-sweet-spot-for-js-apps',
    title: `Snapshot Rendering: The Sweet Spot for JS Apps`,
    releaseDate: '3rd July 2017',
    banner: require('./assets/ep16/banner.png'),
    description: ({ P, Code, Outer }) => <Outer>
      <P>
        As we saw in the last episode, delivering an empty HTML page and relying on JavaScript on the client to render your UI has dramatic implications for both the usability and performance of your app. The golden rule is to always serve HTML, but how does that work exactly? What are the alternatives, and what are the tradeoffs involved?
      </P>
      <P>
        In this episode, we'll dive into the way React makes pre-rendering possible (the good), the additional demands Server-Side Rendering places on your architecture (the bad), and the pitfalls of integrating server-rendering into a modern JS app (the ugly). And we'll see how Snapshot Rendering offers an alternative that plays to the strengths of your existing tooling and infrastructure.
      </P>
    </Outer>
  },
  {
    slug: '17-the-magic-behind-mobx',
    title: `The Magic Behind MobX`,
    releaseDate: '22nd August 2017',
    banner: require('./assets/ep17/banner.png'),
    description: ({ P, Code, Outer }) => <Outer>
      <P>
        MobX is a state management library that's found popularity in the React community—it's often introduced as an alternative to Redux that's easier to work with and reason about, where things "just work". But what about it makes it easy to use?
      </P>
      <P>
        For starters, it follows the Observable pattern—you break your domain into mutable state objects, observers (which map neatly to React components), and actions that only change one thing at once. But Observable libraries are nothing new, in fact it's the exact mental model people are familiar with from working with spreadsheet applications. As a result, a version of this pattern appears in virtually every JS UI framework, as well as Node itself. But MobX still feels like something new. It feels like magic.
      </P>
      <P>
        In this episode, we'll look at the two core mechanics that underlie MobX's beauty—what makes it possible to provide such an "invisible" API. In order to do that, we'll build up the same API from scratch and look at how some lesser-known features of JavaScript itself make things so seamless.
      </P>
    </Outer>
  },
  {
    slug: '18-cleaner-components-with-mobx-and-react',
    title: `Cleaner Components with MobX and React`,
    releaseDate: '31st August 2017',
    banner: require('./assets/ep18/banner.png'),
    description: ({ P, Code, Outer }) => <Outer>
      <P>
        MobX is often talked about as an alternative to Redux for managing state in a React application—and both have their proponents. But where Redux enforces a fairly rigid structure and promises maintainability as a result, MobX offers complete flexibility. But what does that give you? And is it better?
      </P>
      <P>
        In this episode, we start our journey towards answering that question. Armed with an understanding of the MobX's internals from the last episode, we'll experiment with a few different approaches and settle on a really clean delineation between data and UI.
      </P>
    </Outer>
  },
  {
    slug: '19-modelling-state-in-mobx',
    title: `Modelling State in MobX`,
    releaseDate: '30th September 2017',
    banner: require('./assets/ep19/banner.png'),
    description: ({ P, Code, Outer }) => <Outer>
      <P>
        Once you get your head around how MobX works and how it interacts with React components, the question becomes—<strong>how should I structure my stores?</strong> Should I tie my state as close to my React components as possible? Perhaps I should try to recreate the hierarchical structure of something like Redux? Or, maybe I should have a separate store for each domain object?
      </P>
      <P>
        With a library as flexible as MobX, all these designs are possible. And each app has different needs—it's impossible to say that any particular design is "correct" for all cases. So in this episode we'll take an incremental approach—showing how designs change as more requirements come in, how they break down, and how to tease them apart.
      </P>
      <P>
        By the end, we'll have a design that works well for pithy.af and the understanding of the choices that lead us there. Taking inspiration from the model layer from MVC frameworks such as Ruby on Rails, we'll see how MobX lets us present a clean, focussed API with a minimum of boilerplate. One that arguably hasn't really been possible in JavaScript before.
      </P>
    </Outer>
  },
].map((e, i) => Object.assign(e, { number: i + 1 }))

episodes.onHomepage = episodes.reduceRight((obj, e) => {
  if (e.unreleased) obj.upcoming.unshift(e)
  else if (obj.featured || !e.youtube) obj.previous.push(e)
  else obj.featured = e
  return obj
}, { previous: [], upcoming: [] })



/** WEBPACK FOOTER **
 ** ./src/data.js
 **/